import { useCallback, useEffect, useState } from "react";
import {
    Wallet, Loader2, ChevronDown, ChevronUp, Fuel, Shield,
    Wrench, Receipt, Tag,
} from "lucide-react";
import api, { formatPrice } from "../lib/api";

/**
 * Phase 4.23 — Total Cost of Ownership Widget.
 * Zeigt monatliche Gesamtkosten broken-down (Anschaffung · Versicherung ·
 * Sprit · KFZ-Steuer · Wartung) für ein Inserat. Annahmen interaktiv
 * änderbar (Jahres-km, Haltedauer).
 */
const LINE_DEFS = [
    { key: "acquisition", label: "Anschaffung", icon: Tag, color: "text-purple-700" },
    { key: "insurance", label: "Versicherung", icon: Shield, color: "text-blue-700" },
    { key: "fuel", label: "Sprit / Strom", icon: Fuel, color: "text-amber-700" },
    { key: "tax", label: "KFZ-Steuer", icon: Receipt, color: "text-emerald-700" },
    { key: "maintenance", label: "Wartung", icon: Wrench, color: "text-rose-700" },
];

export default function TotalCostOfOwnershipWidget({ listing, onTotalMonthlyChange }) {
    const [open, setOpen] = useState(false);
    const [annualKm, setAnnualKm] = useState(6000);
    const [holdingMonths, setHoldingMonths] = useState(60);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const compute = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.post("/vehicles/tco", {
                price: listing?.price,
                category: listing?.category || "motorcycle",
                fuel: listing?.fuel,
                displacement_ccm: listing?.displacement,
                power_kw: listing?.power_kw,
                emission_class: listing?.emission_class,
                first_registration_date: listing?.first_registration_date,
                condition: listing?.condition,
                annual_km: annualKm,
                holding_months: holdingMonths,
                region_plz: listing?.postal_code,
            });
            setResult(r.data);
            onTotalMonthlyChange?.(r.data?.monthly?.total || 0);
        } catch {
            setResult(null);
        } finally {
            setLoading(false);
        }
    }, [listing, annualKm, holdingMonths, onTotalMonthlyChange]);

    useEffect(() => {
        if (!open) return;
        const t = setTimeout(() => { compute(); }, 250);
        return () => clearTimeout(t);
    }, [open, compute]);

    if (!listing || !listing.price) return null;

    const monthly = result?.monthly;
    const totalMonthly = monthly?.total || 0;

    return (
        <div className="border border-gray-200 rounded-md bg-white" data-testid="tco-widget">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-2 p-4 hover:bg-gray-50 transition-colors text-left"
                data-testid="tco-toggle"
            >
                <Wallet className="w-5 h-5 text-[#FFCC00] shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="text-base font-display font-bold flex items-center gap-2">
                        Gesamtkosten (TCO) — was kostet mich das Fahrzeug wirklich?
                    </div>
                    <div className="text-xs text-gray-500">Anschaffung + Versicherung + Sprit + Steuer + Wartung</div>
                </div>
                {totalMonthly > 0 && (
                    <div className="text-right shrink-0 hidden sm:block">
                        <div className="font-display font-black text-xl text-[#111827] font-mono-num leading-none">{formatPrice(totalMonthly)}</div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mt-0.5">pro Monat</div>
                    </div>
                )}
                <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-sm shrink-0">MOCKED</span>
                {open ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </button>

            {open && (
                <div className="px-5 pb-5 pt-1 border-t border-gray-100" data-testid="tco-body">
                    {/* Annahmen */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                        <div>
                            <label className="text-[10px] uppercase tracking-wider font-bold text-gray-500 block mb-1">Jahres-Fahrleistung: <span className="font-mono-num font-bold">{annualKm.toLocaleString("de-DE")} km</span></label>
                            <input
                                type="range"
                                min="500" max="30000" step="500"
                                value={annualKm}
                                onChange={(e) => setAnnualKm(Number(e.target.value))}
                                className="w-full accent-[#FFCC00]"
                                data-testid="tco-annual-km"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-wider font-bold text-gray-500 block mb-1">Haltedauer: <span className="font-mono-num font-bold">{Math.round(holdingMonths / 12 * 10) / 10} J.</span></label>
                            <input
                                type="range"
                                min="12" max="120" step="6"
                                value={holdingMonths}
                                onChange={(e) => setHoldingMonths(Number(e.target.value))}
                                className="w-full accent-[#FFCC00]"
                                data-testid="tco-holding-months"
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex items-center gap-2 justify-center py-6 text-sm text-gray-500">
                            <Loader2 className="w-4 h-4 animate-spin" /> Berechne…
                        </div>
                    ) : monthly ? (
                        <>
                            {/* Stacked-Bar Visualisierung */}
                            <div className="h-2.5 flex rounded-sm overflow-hidden mb-2" data-testid="tco-stacked-bar">
                                {LINE_DEFS.map(({ key, color }) => {
                                    const v = monthly[key];
                                    const pct = totalMonthly > 0 ? (v / totalMonthly) * 100 : 0;
                                    if (pct < 0.5) return null;
                                    return <div key={key} style={{ width: `${pct}%` }} className={`${color.replace("text-", "bg-")}`} />;
                                })}
                            </div>

                            {/* Breakdown */}
                            <ul className="divide-y divide-gray-100" data-testid="tco-breakdown">
                                {LINE_DEFS.map(({ key, label, icon: Icon, color }) => {
                                    const v = monthly[key] || 0;
                                    return (
                                        <li key={key} className="py-2 flex items-center justify-between text-sm" data-testid={`tco-line-${key}`}>
                                            <span className="flex items-center gap-2"><Icon className={`w-4 h-4 ${color}`} /> {label}</span>
                                            <span className="font-mono-num font-bold">{formatPrice(v)}<span className="text-[10px] text-gray-500 ml-1">/Mon.</span></span>
                                        </li>
                                    );
                                })}
                                <li className="py-2 flex items-center justify-between border-t-2 border-[#111827] mt-1" data-testid="tco-line-total">
                                    <span className="font-display font-black text-base">Gesamt</span>
                                    <span className="font-display font-black text-xl font-mono-num">{formatPrice(monthly.total)}<span className="text-xs text-gray-500 ml-1 font-medium">/Mon.</span></span>
                                </li>
                            </ul>

                            <div className="mt-3 text-[11px] text-gray-500">
                                Annahmen: {result.inputs.consumption_per_100km} {result.inputs.fuel_unit}/100km bei {formatPrice(result.inputs.fuel_price_per_unit)}/{result.inputs.fuel_unit}. {result.disclaimer}
                            </div>
                        </>
                    ) : (
                        <div className="text-sm text-gray-500 py-4 text-center">Keine Berechnung verfügbar.</div>
                    )}
                </div>
            )}
        </div>
    );
}
